import { inject, Injectable, Injector } from '@angular/core';
import { Overlay, OverlayRef, PositionStrategy } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { defer, Observable, of, Subject, switchMap, take, takeUntil, tap } from 'rxjs';
import { nanoid } from 'nanoid';
import { BrowserDrawerComponent } from './internal/browser-drawer/browser-drawer.component';
import { DrawerOptions } from '../../common/interfaces/drawer-options';
import { DrawersService } from '../../common/abstracts/drawers.service';
import { ReturnDrawerData } from '../../common/interfaces/return-drawer-data';
import { DrawerPlacement } from '../../common/constants/drawer-placement';

const DEFAULT_HORIZONTAL_DRAWER_WIDTH = '100%';
const DEFAULT_VERTICAL_DRAWER_HEIGHT = '30%';

type DrawerOptionsWithRequiredInjector<T> = Required<Pick<DrawerOptions<T>, 'injector'>> & DrawerOptions<T>;

@Injectable()
export class BrowserDrawersService extends DrawersService {
  private readonly injector: Injector = inject(Injector);
  private readonly overlay: Overlay = inject(Overlay);

  private drawerRef?: ReturnDrawerData;

  private readonly drawerDataHolder: {
    [id: string]: {
      overlay: OverlayRef;
      drawer: BrowserDrawerComponent<any>;
      afterClose$: Subject<void>;
    };
  } = {};

  closeDrawer$(): Observable<void> {
    return (this.drawerRef?.closeFn$() ?? of(undefined)).pipe(tap(() => (this.drawerRef = undefined)));
  }

  create$<T>(drawerData: DrawerOptionsWithRequiredInjector<T>): Observable<ReturnDrawerData> {
    return defer(() => this.createDrawer(drawerData));
  }

  private createDrawer<T>(drawerData: DrawerOptionsWithRequiredInjector<T>): Observable<ReturnDrawerData> {
    const drawerId = nanoid(5);
    const placement = drawerData.placement || DrawerPlacement.right;

    const width = drawerData.width ?? DEFAULT_HORIZONTAL_DRAWER_WIDTH;
    const height = drawerData.height ?? DEFAULT_VERTICAL_DRAWER_HEIGHT;

    const overlayRef = this.overlay.create({
      hasBackdrop: true,
      backdropClass: 'loop-backdrop',
      panelClass: `loop-drawer-panel-${placement}`,
      positionStrategy: this.getPositionStrategy(placement),
      scrollStrategy: this.overlay.scrollStrategies.block(),
      width: placement === DrawerPlacement.bottom || placement === DrawerPlacement.top ? '100%' : width,
      height: placement === DrawerPlacement.right || placement === DrawerPlacement.left ? '100%' : height
    });

    const drawer: any = overlayRef.attach(
      new ComponentPortal(BrowserDrawerComponent, null, drawerData.injector)
    ).instance;
    drawer.openDrawer(drawerData);

    this.drawerDataHolder[drawerId] = {
      drawer: drawer,
      overlay: overlayRef,
      afterClose$: new Subject<void>()
    };

    this.observeBackdropClick(drawerId);
    this.observeDrawerClosed(drawerId);

    return of({
      afterClose$: this.drawerDataHolder[drawerId].afterClose$,
      closeFn$: () =>
        defer(() => {
          this.closeDrawerById(drawerId);
          return of(undefined);
        })
    });
  }

  private observeBackdropClick(drawerId: string): void {
    this.drawerDataHolder[drawerId].overlay
      .backdropClick()
      .pipe(take(1), takeUntil(this.drawerDataHolder[drawerId].afterClose$))
      .subscribe(() => {
        this.closeDrawerById(drawerId);
      });
  }

  private observeDrawerClosed(drawerId: string): void {
    this.drawerDataHolder[drawerId].drawer.closed.pipe(take(1)).subscribe(() => {
      this.drawerDataHolder[drawerId].overlay.dispose();
      this.drawerDataHolder[drawerId].afterClose$.next();
      this.drawerDataHolder[drawerId].afterClose$.complete();
      delete this.drawerDataHolder[drawerId];
    });
  }

  private closeDrawerById(drawerId: string): void {
    this.drawerDataHolder[drawerId]?.drawer.close();
  }

  private getPositionStrategy(placement: keyof typeof DrawerPlacement): PositionStrategy {
    const globalPositionStrategy = this.overlay.position().global();
    switch (placement) {
      case DrawerPlacement.top:
        return globalPositionStrategy.top('0');
      case DrawerPlacement.left:
        return globalPositionStrategy.left('0');
      case DrawerPlacement.bottom:
        return globalPositionStrategy.bottom('0');
      default:
        return globalPositionStrategy.right('0');
    }
  }
}
